import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { connect } from "react-redux";
import * as Router from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";
import { DashboardLayout } from "component/layout/dashboard";
import { PageHeader } from "component/shared/page-header";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";
import { Page } from "model/page";
import { User } from "model/user";
import { getIntegrationByTypeId, getPages } from "redux/selector";
import { saveUser } from "redux/slice/authentication";
import { fetchPages, savePage } from "redux/slice/pages";
import { RootState } from "redux/store";
import * as kestrelEmbedCode from "shared/kestrel-embed-code";
import { PageProps } from "shared/page-props";
import { IntegrationType } from "type/integration-type";
import { KestrelVersion } from "type/kestrel-version";
import { PageType } from "type/page-type";
import { PlatformChoice } from "type/platform-choice";
import { idxPermitted } from "./access";
import { ConnectIdxWizard } from "./connect-idx-wizard";
import { displayHost } from "./connect-idx-wizard/website-url";
import { ConnectionSection } from "./connection-section";
import { EditPlatformDialog } from "./edit-platform-dialog";
import { SwitchVersionDialog } from "./switch-version-dialog";
import { IdxPagesSection } from "./idx-pages";
import { VersionSection } from "./version-section";
import { showHeadScript, showVersionChoice, upgradeNeedsSetup, versionSwitchGuarded } from "./version-rules";
import { unwrap } from "./unwrap";

interface Props
	extends PageProps,
		Router.RouteComponentProps,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps> {
	user: User;
}

interface State {
	kestrelVersion: KestrelVersion;
	saving: boolean;
	saveSuccess: boolean;
	copied: boolean;
	copyFailed: boolean;
	editPlatformOpen: boolean;
	savingPlatform: boolean;
	/** Shown in the Edit Platform modal when saving the new platform fails. */
	platformError?: string;
	setupOpen: boolean;
	/** A version picked while the switch guard is up, waiting on "Switch versions?". */
	pendingVersion?: KestrelVersion;
	pageSaved: boolean;
}

const mapStateToProps = (state: RootState) => {
	return {
		authLoading: state.authentication.loading,
		authError: state.authentication.error,
		wixIntegration: getIntegrationByTypeId(state, IntegrationType.WIX_APP),
		pages: getPages(state),
	};
};

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators(
		{
			saveUser,
			fetchPages,
			savePage,
		},
		dispatch
	);

// MAX-4419: the IDX half of the old Version & Platform page (/settings/version), which also takes
// over the WordPress Plugin page's registration key, and the SEO Pages page as its IDX Pages table.
class Component extends React.Component<Props, State> {

	public constructor(props: Props) {
		super(props);
		this.state = {
			kestrelVersion: props.user.kestrelVersion,
			saving: false,
			saveSuccess: false,
			copied: false,
			copyFailed: false,
			editPlatformOpen: false,
			savingPlatform: false,
			setupOpen: false,
			pageSaved: false,
		};
	}

	public componentDidMount() {
		this.props.fetchPages();
	}

	public componentDidUpdate(prevProps: Props) {
		const { authLoading, authError } = this.props;
		if (this.state.saving && prevProps.authLoading && !authLoading) {
			this.setState({ saving: false, saveSuccess: !authError });
		}
	}

	private isDirty() {
		return this.state.kestrelVersion !== this.props.user.kestrelVersion;
	}

	private platform() {
		const { user, wixIntegration } = this.props;
		return PlatformChoice.fromWebsitePlatform(user.websitePlatform, !!wixIntegration);
	}

	// Setup is finished once the Default IDX Page and the Search page read Enabled, the way the IDX
	// Pages table reads them: the status page always counts (the table shows it Enabled whatever its
	// stored flag), so it comes down to Search being enabled. An HTML site always gets its pages back,
	// so an empty list means they haven't loaded; treat that as done so the banner doesn't flash an
	// "isn't finished" claim on a site that is.
	private setupComplete() {
		const { pages } = this.props;
		if (pages.length === 0) {
			return true;
		}
		return !!pages.find((page) => page.type === PageType.LISTINGS)?.enabled;
	}

	private handleVersionChange =(kestrelVersion: KestrelVersion) => {
		// Coming back to the saved version undoes a switch, so it never needs confirming.
		if (kestrelVersion !== this.props.user.kestrelVersion && versionSwitchGuarded(this.platform(), this.props.user.kestrelVersion, kestrelVersion)) {
			this.setState({ pendingVersion: kestrelVersion });
		} else {
			this.setState({ kestrelVersion });
		}
	};

	private handleSave = () => {
		const { user, saveUser } = this.props;
		saveUser({ user: { ...user, kestrelVersion: this.state.kestrelVersion } });
		this.setState({ saving: true });
	};

	private handleCancel = () => {
		this.setState({ kestrelVersion: this.props.user.kestrelVersion });
	};

	// The write can be refused (no permission, insecure context), so report success only once it
	// resolves.
	private handleCopyRegistrationKey = async () => {
		try {
			await navigator.clipboard.writeText(this.props.user.activationToken);
			this.setState({ copied: true, copyFailed: false });
		} catch{
			this.setState({ copied: false, copyFailed: true });
		}
	};

	private handleSetUp = () => {
		this.setState({ setupOpen: true });
	};

	// VPLAT 8: a platform change just saves and closes. The page then shows what the new platform
	// needs: the setup banner for an HTML site that isn't set up, Basic setup for Wix without the app,
	// the key for WordPress. The user starts setup from there; nothing opens on its own (Dave's pass).
	private handleSavePlatform = async (choice: PlatformChoice) => {
		const { user, saveUser, wixIntegration } = this.props;
		if (choice === PlatformChoice.fromWebsitePlatform(user.websitePlatform, !!wixIntegration)) {
			this.setState({ editPlatformOpen: false });
			return;
		}
		this.setState({ savingPlatform: true, platformError: undefined });
		try {
			const websitePlatform = choice.toWebsitePlatform(user.websitePlatform);
			await unwrap(saveUser({ user: { ...user, websitePlatform } }));
			this.setState({ savingPlatform: false, editPlatformOpen: false });
			// ihf-root creates a non-WordPress client's IDX pages when they're listed, so a switch to an
			// HTML site needs them fetched before the banner and the IDX Pages table can show.
			this.props.fetchPages();
		} catch{
			this.setState({ savingPlatform: false, platformError: "Couldn't save your platform. Please try again." });
		}
	};

	// The Website URL saves on its own Done, apart from the Save Changes bar, as the platform does (MAX-4576).
	private handleSaveWebsiteUrl = async (baseUrl: string) => {
		const { user, saveUser } = this.props;
		await unwrap(saveUser({ user: { ...user, baseUrl } }));
	};
	
	// Pages save one at a time from the Set Up / Edit modal, apart from the page's own Save Changes bar.
	private handleSavePage = async (page: Page) => {
		await unwrap(this.props.savePage({ page }));
		this.setState({ pageSaved: true });
	};

	public render() {
		const { user, authError, wixIntegration, pages } = this.props;
		const {
			kestrelVersion,
			saving,
			saveSuccess,
			copied,
			copyFailed,
			editPlatformOpen,
			savingPlatform,
			platformError,
			setupOpen,
			pendingVersion,
			pageSaved,
		} = this.state;
		const title = "IDX";
		const v10 = !!user.permissions.kestrelAll && user.kestrelVersion.all;
		// An installed Wix app means a Wix site whatever the stored platform says, as on the old page.
		const platform = PlatformChoice.fromWebsitePlatform(user.websitePlatform, !!wixIntegration);
		const showVersion = showVersionChoice(!!user.permissions.listingDetail, !!wixIntegration);
		const headScript = showHeadScript(platform, !!wixIntegration)
			? kestrelEmbedCode.getSiteEmbedCode(platform.toWebsitePlatform(user.websitePlatform), user.activationToken)
			: undefined;
		// v9 keeps the key for any plugin client, since it replaces their WordPress Plugin page (VPLAT 31).
		const showRegistrationKey = platform.wordpress || (!v10 && !!user.permissions.plugin);
		// WordPress pages belong to the Optima Express plugin, and v9 has no Kestrel pages; Wix gets
		// the table (S4). "Only once connected" waits for SPFF-1/2/3.
		const showPages = v10 && !platform.wordpress;
		const description = v10
			? "Set up IDX to power listings and search on your website. Choose your version, set it up, and add the pages you need."
			: "Set up IDX to power listings and search on your website. Choose your version, then set it up for your platform.";
		return (
			<DashboardLayout
				permitted={user && idxPermitted(user)}
				title={title}
				header={
					<PageHeader
						title={title}
						description={description}
						action={
							user.baseUrl && (
								<Mui.Link href={user.baseUrl} target="_blank" rel="noopener noreferrer">
									View Website <Icons.ExternalLink size={16} />
								</Mui.Link>
							)
						}
					/>
				}
			>
				{showVersion && (
					<VersionSection
						value={kestrelVersion}
						onChange={this.handleVersionChange}
						kestrelAll={!!user.permissions.kestrelAll}
						upgradeNeedsSetup={upgradeNeedsSetup(platform, user.kestrelVersion, kestrelVersion)}
					/>
				)}
				<ConnectionSection
					platform={platform}
					omnipress={!!user.permissions.omnipress}
					v10={v10}
					showRegistrationKey={showRegistrationKey}
					registrationKey={user.activationToken}
					registeredHost={displayHost(user.registeredUrl)}
					websiteUrl={user.baseUrl}
					wixInstalled={!!wixIntegration}
					setupComplete={this.setupComplete()}
					onCopyRegistrationKey={this.handleCopyRegistrationKey}
					onEditPlatform={() => this.setState({ editPlatformOpen: true, platformError: undefined })}
					onSetUp={this.handleSetUp}
					onSaveWebsiteUrl={this.handleSaveWebsiteUrl}
				/>
				{showPages && (
					<IdxPagesSection
						pages={pages}
						baseUrl={user.baseUrl}
						// The embed code wants the stored platform's variant (Wix gets the text embed).
						platform={platform.toWebsitePlatform(user.websitePlatform)}
						onSavePage={this.handleSavePage}
						headScript={headScript}
					/>
				)}
				<EditPlatformDialog
					open={editPlatformOpen}
					value={platform}
					saving={savingPlatform}
					error={platformError}
					onClose={() => this.setState({ editPlatformOpen: false })}
					onSave={this.handleSavePlatform}
				/>
				<SwitchVersionDialog
					open={!!pendingVersion}
					onSwitch={() => this.setState({ kestrelVersion: pendingVersion ?? kestrelVersion, pendingVersion: undefined })}
					onCancel={() => this.setState({ pendingVersion: undefined })}
				/>
				<ConnectIdxWizard
					open={setupOpen}
					onClose={() => this.setState({ setupOpen: false })}
					user={user}
				/>
				<SettingsStickyFooter
					refreshed
					dirty={this.isDirty()}
					saving={saving}
					authError={authError}
					saveSuccess={saveSuccess}
					onSave={this.handleSave}
					onCancel={this.handleCancel}
					onSaveFeedbackClose={() => this.setState({ saveSuccess: false })}
				/>
				<Mui.Snackbar
					open={pageSaved}
					autoHideDuration={4000}
					onClose={() => this.setState({ pageSaved: false })}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				>
					<Mui.Alert severity="success" onClose={() => this.setState({ pageSaved: false })}>
						Page saved
					</Mui.Alert>
				</Mui.Snackbar>
				<Mui.Snackbar
					open={copied || copyFailed}
					autoHideDuration={4000}
					onClose={() => this.setState({ copied: false, copyFailed: false })}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				>
					<Mui.Alert
						severity={copyFailed ? "error" : "success"}
						onClose={() => this.setState({ copied: false, copyFailed: false })}
					>
						{copyFailed ? "Couldn't copy. Select the key and copy it manually." : "Copied to clipboard"}
					</Mui.Alert>
				</Mui.Snackbar>
				<UnsavedChangesPrompt isDirty={() => this.isDirty()} history={this.props.history} />
			</DashboardLayout>
		);
	}

}

export const SettingsIdxPage = connect(mapStateToProps, mapDispatchToProps)(Component);
